import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Paginated, Resource } from '@/lib/api/types'

/** A group's filters: the customer list's, plus who in each workspace and its connections. */
export interface GroupFilters {
  who?: 'owners' | 'everyone'
  plan?: string
  billing?: string
  signed_up?: string
  activity?: string
  tag?: string
  segment?: string
  market?: string
  channel?: string
  wordpress?: string
  google?: string
  ai?: string
}

export type FilterKey = keyof GroupFilters

export type CampaignStatus = 'draft' | 'sending' | 'sent'

/** Marketing goes to people who opted in; a service notice to every customer in the group. */
export type CampaignKind = 'marketing' | 'service'

export interface CampaignResults {
  progress: Record<'sent' | 'queued' | 'skipped' | 'failed', number>
  /** Whether opens and clicks are counted: marketing emails yes, service notices no. */
  counts_opens: boolean
  /** The emails Amazon SES could count opens and clicks for: 0 for a service notice. */
  tracked: number
  opened: number | null
  clicked: number | null
  bounced: number
  complained: number
  unsubscribed: number | null
  /** A marketing email sent a day ago or more, to 20 people or more, with no open or click recorded. */
  tracking_warning: boolean
  /** Purchases by the workspaces of people who got it, in the days after (and after a click, when tracked). */
  purchases: {
    days: number
    workspaces: number
    revenue_usd: number
    after_click_workspaces: number | null
    after_click_revenue_usd: number | null
  }
}

export interface Campaign {
  id: string
  kind: CampaignKind
  name: string
  subject: string
  preheader: string | null
  body: string
  button_label: string | null
  button_url: string | null
  filters: GroupFilters
  group_id: string | null
  skip_recent_days: number
  status: CampaignStatus
  recipients: number
  results: CampaignResults | null
  updated_by: string | null
  sent_by: string | null
  sent_at: string | null
  scheduled_at: string | null
  scheduled_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface MarketingGroup {
  id: string
  name: string
  filters: GroupFilters
  /** How many people it reaches now (ignoring "emailed recently"). */
  count: number
  updated_at: string | null
}

export interface MarketingList {
  data: Campaign[]
  groups: MarketingGroup[]
  meta: {
    kinds: Record<CampaignKind, { label: string; description: string }>
    /** Workspace owners who opted in, and everyone on a team who did. */
    opted_in: number
    everyone_opted_in: number
    /** Workspace owners a service notice reaches, opted in or not. */
    customers: number
    merge_tags: Record<string, string>
    conversion_days: number
    /** Each filter's choices, as the API knows them (the page's own list when one is missing). */
    filters: Partial<Record<FilterKey, string[]>>
    plans: { key: string; name: string }[]
  }
}

export type CampaignInput = Pick<
  Campaign,
  | 'kind'
  | 'name'
  | 'subject'
  | 'preheader'
  | 'body'
  | 'button_label'
  | 'button_url'
  | 'filters'
  | 'group_id'
  | 'skip_recent_days'
>

export function useMarketing() {
  return useQuery({
    queryKey: ['marketing'],
    queryFn: () => http.get<MarketingList>('/marketing'),
  })
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['marketing', id],
    enabled: id !== undefined,
    queryFn: async () => (await http.get<Resource<Campaign>>(`/marketing/${id ?? ''}`)).data,
    // While it goes out, its progress updates by itself.
    refetchInterval: (query) => (query.state.data?.status === 'sending' ? 3000 : false),
  })
}

function useCampaignMutation<T>(fn: (input: T) => Promise<Campaign>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (c) => {
      queryClient.setQueryData(['marketing', c.id], c)
      void queryClient.invalidateQueries({ queryKey: ['marketing'], exact: true })
    },
  })
}

export function useSaveCampaign(id: string | undefined) {
  return useCampaignMutation(async (input: CampaignInput) =>
    id === undefined
      ? (await http.post<Resource<Campaign>>('/marketing', input)).data
      : (await http.patch<Resource<Campaign>>(`/marketing/${id}`, input)).data,
  )
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/marketing/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['marketing'] }),
  })
}

/** Sends it now, after confirming how many people it reaches: owners only, once. */
export function useSendCampaign(id: string) {
  return useCampaignMutation(
    async (recipients: number) =>
      (await http.post<Resource<Campaign>>(`/marketing/${id}/send`, { confirm_recipients: recipients })).data,
  )
}

/** Sends it later: owners only; it stays a draft until then. */
export function useScheduleCampaign(id: string) {
  return useCampaignMutation(
    async (sendAt: string) =>
      (await http.post<Resource<Campaign>>(`/marketing/${id}/schedule`, { send_at: sendAt })).data,
  )
}

export function useUnscheduleCampaign(id: string) {
  return useCampaignMutation(
    async () => (await http.delete<Resource<Campaign>>(`/marketing/${id}/schedule`)).data,
  )
}

/** How many people a group reaches now, with a few of their addresses. */
export function useAudience(
  filters: GroupFilters,
  skipRecentDays: number,
  campaign?: string,
  kind: CampaignKind = 'marketing',
) {
  return useQuery({
    queryKey: ['marketing', 'audience', filters, skipRecentDays, campaign, kind],
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await http.post<Resource<{ count: number; sample: string[] }>>('/marketing/audience', {
          filters,
          skip_recent_days: skipRecentDays,
          campaign,
          kind,
        })
      ).data,
  })
}

type Content = Pick<CampaignInput, 'kind' | 'subject' | 'preheader' | 'body' | 'button_label' | 'button_url'>

/** The email as it will look, with made-up details (rendered by the API, so it's exact). */
export function useMarketingPreview(content: Content, enabled: boolean) {
  return useQuery({
    queryKey: ['marketing', 'preview', content],
    enabled: enabled && content.subject.trim() !== '' && content.body.trim() !== '',
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await http.post<Resource<{ subject: string; html: string }>>('/marketing/preview', content)).data,
  })
}

export function useSendTestMarketing() {
  return useMutation({
    mutationFn: async (content: Content) =>
      (await http.post<Resource<{ sent_to: string }>>('/marketing/test', content)).data,
  })
}

export function useSaveGroup(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; filters: GroupFilters }) =>
      id === undefined
        ? (await http.post<Resource<MarketingGroup>>('/marketing-groups', input)).data
        : (await http.patch<Resource<MarketingGroup>>(`/marketing-groups/${id}`, input)).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['marketing'], exact: true }),
  })
}

export function useDeleteGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/marketing-groups/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['marketing'], exact: true }),
  })
}

/** One person's copy of a marketing email. */
export interface MarketingSend {
  id: number
  campaign: string | null
  campaign_id: string
  /** False for a service notice's copies: their opens and clicks aren't counted. */
  counts_opens: boolean
  to: string | null
  status: 'queued' | 'sent' | 'skipped' | 'failed'
  reason: string | null
  queued_at: string | null
  sent_at: string | null
  opened_at: string | null
  clicked_at: string | null
  bounced_at: string | null
  complained_at: string | null
  unsubscribed_at: string | null
}

export type MarketingSendFilter = MarketingSend['status'] | 'all'

/** Copies of every marketing email (or one), newest first, 25 a page. Refreshes while any wait. */
export function useMarketingSends(
  status: MarketingSendFilter,
  campaign: string,
  search: string,
  page: number,
) {
  return useQuery({
    queryKey: ['marketing', 'sends', status, campaign, search, page],
    queryFn: () =>
      http.get<Paginated<MarketingSend>>('/marketing/sends', {
        status: status === 'all' ? undefined : status,
        campaign,
        search,
        page,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.data.some((s) => s.status === 'queued') ? 4000 : false),
  })
}
